import { useMemo } from 'react';
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Alert,
  Box,
  Grid,
  LinearProgress,
  Typography,
} from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import { useTranslation } from 'react-i18next';
import { StatusBadge } from './StatusBadge';
import { LoadingSkeleton } from './LoadingSkeleton';
import { SectionCard } from './SectionCard';
import { DataTable } from './DataTable';
import { InfoCard } from './InfoCard';
import { colorTokens } from '../app/designTokens';
import {
  formatCheckStatus,
  formatReadinessCheckTitle,
  formatReadinessMessage,
  formatReadinessStatus,
  safeTranslate,
} from '../utils/systemLabels';

const { semantic } = colorTokens;

const SEVERITY_GROUPS = [
  { key: 'blocker', severity: 'blocker', titleKey: 'tenants:readiness.groups.blockers' },
  { key: 'warning', severity: 'warning', titleKey: 'tenants:readiness.groups.warnings' },
  { key: 'passed', severity: 'passed', titleKey: 'tenants:readiness.groups.passed' },
  { key: 'deferred', severity: 'deferred', titleKey: 'tenants:readiness.groups.deferred' },
];

function translateCheckStatus(t, status) {
  return formatCheckStatus(status) || safeTranslate(t, `statuses:check.${status}`, status);
}

function ChecksTable({ checks, t }) {
  if (!checks?.length) {
    return <Typography variant="body2" color="text.secondary">{t('common:empty_value')}</Typography>;
  }

  const columns = [
    {
      id: 'check',
      label: t('tenants:check'),
      render: (check) => formatReadinessCheckTitle(check),
    },
    {
      id: 'status',
      label: t('common:status'),
      render: (check) => (
        <StatusBadge
          status={check.status}
          apiLabel={translateCheckStatus(t, check.status)}
          size="Sm"
          tone={
            check.status === 'passed' ? 'Success'
              : check.status === 'warning' ? 'Warning'
                : (check.status === 'failed' || check.status === 'blocked') ? 'Danger'
                  : check.status === 'pending' ? 'Info'
                    : 'Neutral'
          }
        />
      ),
    },
    {
      id: 'message',
      label: t('common:message'),
      render: (check) => formatReadinessMessage(check) || check.message || '—',
    },
  ];

  return (
    <DataTable
      type="Compact"
      embedded
      columns={columns}
      rows={checks}
      getRowKey={(check) => check.key}
    />
  );
}

export function TenantReadinessPanel({ readiness, loading }) {
  const { t } = useTranslation(['tenants', 'common', 'statuses']);

  const groupedChecks = useMemo(() => {
    const groups = {
      blocker: [],
      warning: [],
      passed: [],
      deferred: [],
      pending: [],
      info: [],
    };

    (readiness?.checks || []).forEach((check) => {
      const severity = check.severity || check.status;
      if (groups[severity]) {
        groups[severity].push(check);
      } else {
        groups.info.push(check);
      }
    });

    return groups;
  }, [readiness]);

  if (loading) {
    return <LoadingSkeleton type="Detail" />;
  }

  if (!readiness) {
    return <Alert severity="info">{t('tenants:readiness_unavailable')}</Alert>;
  }

  const progress = readiness.total_count
    ? Math.round((readiness.completed_count / readiness.total_count) * 100)
    : 0;

  const summary = readiness.severity_summary || {};

  return (
    <Box>
      <SectionCard variant="Default" sx={{ mb: 2 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 1, flexWrap: 'wrap' }}>
          <Typography
            sx={{
              fontSize: 14,
              fontWeight: 600,
              color: semantic.text.primary,
            }}
          >
            {t('tenants:overall_readiness')}
          </Typography>
          <StatusBadge
            status={readiness.overall_status}
            apiLabel={formatReadinessStatus(readiness.overall_status) || readiness.overall_status}
            size="Sm"
            tone={
              readiness.overall_status === 'ready' ? 'Success'
                : readiness.overall_status === 'ready_for_manual_review' ? 'Info'
                  : readiness.overall_status === 'partially_ready' ? 'Warning'
                    : readiness.overall_status === 'not_ready' ? 'Danger'
                      : 'Neutral'
            }
          />
        </Box>
        <Typography sx={{ mb: 1.5, fontSize: 13, color: semantic.text.secondary }}>
          {t('tenants:checks_passed', {
            completed: readiness.completed_count,
            total: readiness.total_count,
            score: readiness.overall_score,
          })}
        </Typography>
        <LinearProgress
          variant="determinate"
          value={progress}
          sx={{
            mb: 2,
            height: 6,
            borderRadius: 999,
            bgcolor: semantic.action.secondary.bg,
            '& .MuiLinearProgress-bar': {
              bgcolor: semantic.status.brand.border,
            },
          }}
        />

        <Grid container spacing={2}>
          <Grid item xs={6} md={3}>
            <InfoCard
              label={t('tenants:readiness.summary.blockers')}
              value={summary.blockers ?? groupedChecks.blocker.length}
            />
          </Grid>
          <Grid item xs={6} md={3}>
            <InfoCard
              label={t('tenants:readiness.summary.warnings')}
              value={summary.warnings ?? groupedChecks.warning.length}
            />
          </Grid>
          <Grid item xs={6} md={3}>
            <InfoCard
              label={t('tenants:readiness.summary.passed')}
              value={summary.passed ?? groupedChecks.passed.length}
            />
          </Grid>
          <Grid item xs={6} md={3}>
            <InfoCard
              label={t('tenants:readiness.summary.deferred')}
              value={summary.deferred ?? groupedChecks.deferred.length}
            />
          </Grid>
        </Grid>
      </SectionCard>

      {SEVERITY_GROUPS.map((group) => (
        groupedChecks[group.severity]?.length > 0 && (
          <SectionCard
            key={group.key}
            variant={
              group.severity === 'blocker' ? 'Danger'
                : group.severity === 'warning' ? 'Warning'
                  : 'Default'
            }
            sx={{ mb: 2 }}
          >
            <Accordion
              defaultExpanded={group.severity === 'blocker'}
              disableGutters
              elevation={0}
              sx={{
                bgcolor: 'transparent',
                backgroundImage: 'none',
                '&:before': { display: 'none' },
              }}
            >
              <AccordionSummary expandIcon={<ExpandMoreIcon sx={{ color: semantic.text.muted }} />}>
                <Typography sx={{ fontSize: 14, fontWeight: 600, color: semantic.text.primary }}>
                  {t(group.titleKey)} ({groupedChecks[group.severity].length})
                </Typography>
              </AccordionSummary>
              <AccordionDetails sx={{ px: 0, pb: 0 }}>
                <ChecksTable checks={groupedChecks[group.severity]} t={t} />
              </AccordionDetails>
            </Accordion>
          </SectionCard>
        )
      ))}
    </Box>
  );
}

export function ReadinessStatusBadge({ status }) {
  if (!status) {
    return null;
  }

  return <StatusBadge status={status} />;
}
